"use client";

import { c as _c } from "react/compiler-runtime";
import { CustomLink } from "@repo/design-system/components/ui/custom-link";
import { useFilterSearch } from "@repo/design-system/hooks/use-filter-search";
import { useLocale } from "@repo/design-system/hooks/use-locale";
import { type ResponsiveColumns, useResponsiveColumns } from "@repo/design-system/hooks/use-responsive-columns";
import { matchLocale } from "@repo/design-system/lib/middleware/handle-locale";
import { cn } from "@repo/design-system/utils/cn";
import { getJobAdUrlFromData } from "@repo/design-system/utils/helpers";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { Button } from "~/components/ui/button-eon";
import { LocationDisplay } from "~/components/ui/location-display-eon";
import { toSimilarJob } from "~/lib/utils/to-similar-job";
import { i18n } from "~/setup/locales";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobExcerpt } from "~/types/job";
import { WatchJobButton } from "../../../ui/watch-job-button-eon";
import { JobListSkeleton } from "../../skeletons-eon";
export function JoblistEonSverige() {
  "use no memo";

  const t = useLanguage();
  const locale = useLocale();
  const {
    data: jobs,
    isLoading,
    isFetchingNextPage,
    fetchNextPage
  } = useFilterSearch();
  const allJobs = useMemo(() => (jobs?.pages.flatMap(page => page.jobs) || []) as JobExcerpt[], [jobs]);
  const totalHits = jobs?.pages[0].totalHits || 0;
  const getJobLink = (data: JobExcerpt["data"]) => ({
    href: `/sverige${getJobAdUrlFromData(data)}`,
    locale: matchLocale([data.language], i18n.locales, locale)
  });
  const columnDef: ResponsiveColumns<JobExcerpt> = [
  // Large screens: title only — the location gets its own column
  {
    hideOn: ["sm", "md"],
    header: t.joblist_header1,
    id: "title",
    cell: ({
      row: {
        original: {
          data: data_0
        }
      }
    }) => <CustomLink blank className="flex w-full flex-col" passParams {...getJobLink(data_0)}>
          <p className="font-bold text-primary text-xl" lang={data_0.language?.toLowerCase()}>
            {data_0.title}
          </p>
        </CustomLink>
  },
  // Small/medium screens: title and location stacked in one column
  {
    hideOn: ["lg"],
    header: t.joblist_header1,
    id: "titleWithLocation",
    cell: ({
      row: {
        original,
        original: {
          data: data_1
        }
      }
    }) => <CustomLink blank className="flex max-w-full flex-col break-words" passParams {...getJobLink(data_1)}>
          <span className="font-bold text-primary text-xl" lang={data_1.language?.toLowerCase()}>
            {data_1.title}
          </span>
          <span className="text-base text-inherit">
            <LocationDisplay job={original} />
          </span>
        </CustomLink>
  }, {
    hideOn: ["sm", "md"],
    header: t.joblist_header2,
    cell: ({
      row
    }) => <CustomLink blank className="block h-full w-full" passParams tabIndex={-1} {...getJobLink(row.original.data)}>
          <LocationDisplay job={row.original} />
        </CustomLink>
  }, {
    header: t.joblist_header4,
    id: t.joblist_header4,
    cell: ({
      row: row_0
    }) => <div className="flex w-full justify-center">
          <WatchJobButton className="text-foreground hover:text-primary" jobData={toSimilarJob(row_0.original.data)} variant="icon" />
        </div>
  }];
  const columns = useResponsiveColumns(columnDef);
  const table = useReactTable({
    data: allJobs,
    columns,
    getCoreRowModel: getCoreRowModel()
  });
  if (isLoading) {
    return <JobListSkeleton />;
  }
  return <section className="outer-container text-lg">
      <div className="inner-container">
        <h2 className="sr-only">{t.aria_header_joblist}</h2>
        <table className="my-8 w-full table-fixed border-collapse">
          <thead className="border-b-2">
            {table.getHeaderGroups().map(headerGroup => <tr className="grid grid-cols-[90%_10%] items-center justify-items-start py-6 pr-2 sm:pr-0 lg:grid-cols-[55%_35%_10%]" key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => <th className={cn("flex w-full px-4", index === headerGroup.headers.length - 1 && "justify-center")} key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>)}
              </tr>)}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row_1 => <tr className="grid grid-cols-[90%_10%] border-b py-6 pr-4 transition-colors duration-250 hover:bg-gray-200 sm:pr-0 lg:grid-cols-[55%_35%_10%]" key={row_1.id}>
                {row_1.getVisibleCells().map(cell => <td className="w-full px-4" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
        <div className="flex w-full items-center justify-center">
          <LoadMoreButton fetchNextPage={fetchNextPage} isFetchingNextPage={isFetchingNextPage} jobsPerPage={jobs?.pages[0].jobsPerPage ?? 0} pagesLength={jobs?.pages.length ?? 0} totalHits={totalHits} />
        </div>
      </div>
    </section>;
}
function LoadMoreButton(t0) {
  const $ = _c(11);
  const {
    isFetchingNextPage,
    jobsPerPage,
    pagesLength,
    totalHits,
    fetchNextPage
  } = t0;
  const t = useLanguage();
  const locale = useLocale();
  const loadedJobs = Math.min(Number(pagesLength) * Number(jobsPerPage), Number(totalHits));
  const hasMoreJobs = loadedJobs < totalHits;
  if (!hasMoreJobs) {
    return null;
  }
  let t1;
  if ($[0] !== isFetchingNextPage || $[1] !== loadedJobs || $[2] !== locale || $[3] !== t.joblist_loadMore1 || $[4] !== t.joblist_loadMore2 || $[5] !== totalHits) {
    t1 = isFetchingNextPage ? t.joblist_loadMore1 || "Loading..." : `${t.joblist_loadMore1} (${loadedJobs.toLocaleString(locale)} ${t.joblist_loadMore2} ${totalHits.toLocaleString(locale)})`;
    $[0] = isFetchingNextPage;
    $[1] = loadedJobs;
    $[2] = locale;
    $[3] = t.joblist_loadMore1;
    $[4] = t.joblist_loadMore2;
    $[5] = totalHits;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== fetchNextPage || $[8] !== isFetchingNextPage || $[9] !== t1) {
    t2 = <Button className="my-12" disabled={isFetchingNextPage} onClick={fetchNextPage}>{t1}</Button>;
    $[7] = fetchNextPage;
    $[8] = isFetchingNextPage;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}